import React from 'react';

import { ICellRendererParams } from 'ag-grid-community';
import { Link } from 'react-router-dom';
import { EXCEL_DATE_STYLE_ID, LINK_COLOR } from 'src/constants/agGrid';
import { ROUTES } from 'src/constants/routes';
import { emptyCellFormatter } from 'src/util/grid/cellFormatter';
import {
    getIsCompleteValue,
    getIsPromoPlan,
    getLastUpdatedBy,
    getLastUpdatedYYYYMMDDStrFromTimestamp,
    getPlanGroupForPaperPlan,
    getPlanStartEndDate,
    getPlanTerritory,
    getPromoDurationDays,
    getRateGroupForPaperPlan,
    getStatusForPaperPlan,
    getYYYYMMDDStrFromTimestamp
} from 'src/util/grid/cellGetter';
import {
    getPaperPlanBooleanAttributeRenderer,
    getPhoneToolLinkCellRenderer,
    getStatusCellRenderer,
    getTerritoryFlagRenderer
} from 'src/util/grid/cellRenderer';
import { dateFilterOptions } from 'src/util/grid/filter';
import { dateComparator, dateFilterComparator } from 'src/util/grid/sort';

export const paperPlansColDefs = [
    {
        field: 'paperPlanName',
        headerName: 'PAPER plan ID',
        filter: 'agSetColumnFilter',
        cellRenderer: (params: any) => {
            return (
                <Link
                    style={{ color: LINK_COLOR, textDecoration: 'none' }}
                    to={`${ROUTES.PAPER_PLANS.INDEX}/${params.data.paperPlanId}`}
                >
                    {params.value}
                </Link>
            );
        },
        width: 400,
        minWidth: 300,
        pinned: true
    },
    {
        field: 'displayName',
        headerName: 'Display Name',
        filter: 'agSetColumnFilter',
        width: 300,
        minWidth: 200
    },
    {
        field: 'reportingName',
        headerName: 'External Reporting Name',
        sortable: true,
        filter: true,
        width: 300,
        minWidth: 200
    },
    {
        field: 'subscriptionProduct',
        headerName: 'Subscription Product',
        sortable: true,
        filter: true,
        width: 300,
        minWidth: 200
    },
    {
        field: 'affiliateName',
        headerName: 'Promo Affiliate Name',
        sortable: true,
        filter: true,
        width: 300,
        minWidth: 200
    },
    {
        field: 'description',
        headerName: 'Description',
        filter: 'agSetColumnFilter',
        width: 300,
        minWidth: 230
    },
    {
        field: 'planType',
        headerName: 'Plan Type',
        filter: 'agSetColumnFilter',
        minWidth: 150
    },
    {
        field: 'planGroup',
        headerName: 'Plan Group',
        valueGetter: getPlanGroupForPaperPlan,
        filter: 'agSetColumnFilter',
        minWidth: 150
    },
    {
        field: 'rateGroup',
        headerName: 'Rate Group',
        valueGetter: getRateGroupForPaperPlan,
        hide: true
    },
    {
        field: 'renewalCadence',
        headerName: 'Renewal Cadence',
        valueFormatter: emptyCellFormatter,
        minWidth: 180
    },
    {
        field: 'promoDurationInDays',
        headerName: 'Promo Duration (in days)',
        valueGetter: getPromoDurationDays,
        valueFormatter: emptyCellFormatter,
        minWidth: 220
    },
    {
        field: 'status',
        headerName: 'Status',
        valueGetter: getStatusForPaperPlan,
        cellRenderer: getStatusCellRenderer,
        cellRendererParams: {
            displayText: true
        },
        minWidth: 120
    },
    {
        field: 'isDraft',
        headerName: 'Complete',
        valueGetter: getIsCompleteValue,
        cellRenderer: getPaperPlanBooleanAttributeRenderer,
        minWidth: 120
    },
    {
        field: 'creationDate',
        headerName: 'Creation Date',
        comparator: dateComparator,
        filter: 'agDateColumnFilter',
        filterParams: {
            filterOptions: dateFilterOptions,
            defaultOption: 'greaterThanOrEqual',
            comparator: dateFilterComparator,
            includeBlanksInLessThan: true,
            includeBlanksInGreaterThan: true
        },
        cellClass: EXCEL_DATE_STYLE_ID,
        valueFormatter: emptyCellFormatter,
        valueGetter: getYYYYMMDDStrFromTimestamp,
        hide: true
    },
    {
        field: 'createdBy',
        headerName: 'Created By',
        filter: 'agSetColumnFilter',
        cellRenderer: getPhoneToolLinkCellRenderer,
        hide: true
    },
    {
        field: 'changedDate',
        headerName: 'Last updated',
        comparator: dateComparator,
        filter: 'agDateColumnFilter',
        filterParams: {
            filterOptions: dateFilterOptions,
            defaultOption: 'greaterThanOrEqual',
            comparator: dateFilterComparator,
            includeBlanksInLessThan: true,
            includeBlanksInGreaterThan: true
        },
        cellClass: EXCEL_DATE_STYLE_ID,
        valueFormatter: emptyCellFormatter,
        valueGetter: getLastUpdatedYYYYMMDDStrFromTimestamp,
        minWidth: 150
    },
    {
        field: 'changedBy',
        headerName: 'Last updated By',
        filter: 'agSetColumnFilter',
        valueGetter: getLastUpdatedBy,
        cellRenderer: getPhoneToolLinkCellRenderer,
        minWidth: 180
    },
    {
        field: 'territory',
        headerName: 'Territory',
        valueGetter: getPlanTerritory,
        cellRenderer: (params: ICellRendererParams) => getTerritoryFlagRenderer(params, false),
        hide: true
    },
    {
        field: 'startDate',
        headerName: 'Start Date',
        comparator: dateComparator,
        filter: 'agDateColumnFilter',
        filterParams: {
            filterOptions: dateFilterOptions,
            defaultOption: 'greaterThanOrEqual',
            comparator: dateFilterComparator,
            includeBlanksInLessThan: true,
            includeBlanksInGreaterThan: true
        },
        cellClass: EXCEL_DATE_STYLE_ID,
        valueGetter: getPlanStartEndDate,
        hide: true
    },
    {
        field: 'endDate',
        headerName: 'End Date',
        comparator: dateComparator,
        filter: 'agDateColumnFilter',
        filterParams: {
            filterOptions: dateFilterOptions,
            defaultOption: 'lessThanOrEqual',
            comparator: dateFilterComparator,
            includeBlanksInLessThan: true,
            includeBlanksInGreaterThan: true
        },
        cellClass: EXCEL_DATE_STYLE_ID,
        valueFormatter: emptyCellFormatter,
        valueGetter: getPlanStartEndDate,
        hide: true
    },
    {
        field: 'isPromo',
        headerName: 'isPromo',
        valueGetter: getIsPromoPlan,
        cellRenderer: getPaperPlanBooleanAttributeRenderer,
        hide: true
    },
    {
        field: 'planType',
        headerName: 'Plan Type',
        valueFormatter: emptyCellFormatter,
        hide: true
    },
    {
        field: 'employeePlan',
        headerName: 'isEmployeePlan',
        cellRenderer: getPaperPlanBooleanAttributeRenderer,
        hide: true
    },
    {
        field: 'carrierPlan',
        headerName: 'isCarrierPlan',
        cellRenderer: getPaperPlanBooleanAttributeRenderer,
        hide: true
    },
    {
        field: 'royaltyBearing',
        headerName: 'isRoyaltyBearing',
        cellRenderer: getPaperPlanBooleanAttributeRenderer,
        hide: true
    },
    {
        field: 'customerPays',
        headerName: 'CustomerPays',
        cellRenderer: getPaperPlanBooleanAttributeRenderer,
        hide: true
    },
    {
        field: 'isBundle',
        headerName: 'isBundle',
        cellRenderer: getPaperPlanBooleanAttributeRenderer,
        hide: true
    },
    {
        field: 'bundleType',
        headerName: 'Bundle Type',
        valueFormatter: emptyCellFormatter,
        hide: true
    },
    {
        field: 'wholesalePrice',
        headerName: 'Wholesale Price',
        valueFormatter: emptyCellFormatter,
        hide: true
    },
    {
        field: 'wholesalePriceCurrency',
        headerName: 'Wholesale Price Currency',
        valueFormatter: emptyCellFormatter,
        hide: true
    },
    {
        field: 'endUserPrice',
        headerName: 'End User Price',
        valueFormatter: emptyCellFormatter,
        hide: true
    },
    {
        field: 'endUserPriceCurrency',
        headerName: 'End User Price Currency',
        valueFormatter: emptyCellFormatter,
        hide: true
    },
    {
        field: 'bundlePrice',
        headerName: 'Bundle Price',
        valueFormatter: emptyCellFormatter,
        hide: true
    },
    {
        field: 'bundlePriceCurrency',
        headerName: 'Bundle Price Currency',
        valueFormatter: emptyCellFormatter,
        hide: true
    },
    {
        field: 'nonBundledMusicPrice',
        headerName: 'Non Bundled Music Price',
        valueFormatter: emptyCellFormatter,
        hide: true
    },
    {
        field: 'nonBundledMusicPriceCurrency',
        headerName: 'Non Bundled Music Price Currency',
        valueFormatter: emptyCellFormatter,
        hide: true
    },
    {
        field: 'nonBundledNonMusicPrice',
        headerName: 'Non Bundled Nonmusic Price',
        valueFormatter: emptyCellFormatter,
        hide: true
    },
    {
        field: 'nonBundledNonMusicPriceCurrency',
        headerName: 'Non Bundled Nonmusic Price Currency',
        valueFormatter: emptyCellFormatter,
        hide: true
    }
];
